import { Mutations } from '@/store/enums/StoreEnums';
// used as @Module/@Mutation decorators
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Module, Mutation, VuexModule } from 'vuex-module-decorators';

export interface AppSettings {
  [key: string]: unknown;
}

export interface AppConfigurationState {
  settings: AppSettings;
  isDefault: boolean;
}

// localStorage key for app settings
const APP_SETTINGS_LS_KEY = 'app_settings';

// Helper functions for localStorage
const getAppSettingsFromStorage = (): {
  settings: AppSettings;
  isDefault: boolean;
} => {
  try {
    const stored = localStorage.getItem(APP_SETTINGS_LS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { settings: parsed, isDefault: Object.keys(parsed).length === 0 };
    }
    return { settings: {}, isDefault: true };
  } catch (error) {
    console.warn('Failed to parse app settings from localStorage:', error);
    return { settings: {}, isDefault: true };
  }
};

const saveAppSettingsToStorage = (settings: AppSettings): void => {
  try {
    localStorage.setItem(APP_SETTINGS_LS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to save app settings to localStorage:', error);
  }
};

@Module
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default class AppConfigurationModule
  extends VuexModule
  implements AppConfigurationState
{
  private _initialized = getAppSettingsFromStorage();
  settings: AppSettings = this._initialized.settings;
  isDefault = this._initialized.isDefault;

  get getSettings(): AppSettings {
    return this.settings;
  }

  get getIsDefault(): boolean {
    return this.isDefault;
  }

  get getSetting() {
    return (key: string, defaultValue?: unknown) => {
      return this.settings[key] !== undefined
        ? this.settings[key]
        : defaultValue;
    };
  }

  get isBBGTApprovalRequired(): boolean {
    // This getter is kept for backward compatibility
    // It checks if any department has approval level > 0
    let needApproval = false;

    Object.keys(this.settings).forEach((key: string) => {
      if (key.startsWith("OPT_BBGT_REQUIRED_APPROVAL_")) {
        const level = parseInt(this.settings[key] as string) || 0;
        if (level > 0) {
          needApproval = true;
        }
      }
    });

    return needApproval;
  }

  /**
   * Get BBGT approval level for a specific department
   * @param idDonVi - Department ID
   * @returns Approval level: 0 (no approval), 1 (approve only), 2 (confirm + approve)
   */
  get getBBGTApprovalLevel() {
    return (idDonVi: number): number => {
      if (!idDonVi) {
        return 0;
      }

      const key = `OPT_BBGT_REQUIRED_APPROVAL_${idDonVi}`;
      const value = this.settings[key];

      if (value === undefined || value === null) {
        return 0;
      }

      const level = parseInt(value as string) || 0;
      return level;
    };
  }

  @Mutation
  [Mutations.SET_APP_SETTINGS](settings: AppSettings) {
    this.settings = { ...settings };
    this.isDefault = false;
    saveAppSettingsToStorage(this.settings);
  }

  @Mutation
  [Mutations.MERGE_APP_SETTINGS](settings: AppSettings) {
    this.settings = { ...this.settings, ...settings };
    this.isDefault = false;
    saveAppSettingsToStorage(this.settings);
  }

  @Mutation
  [Mutations.RESET_APP_SETTINGS]() {
    this.settings = {};
    this.isDefault = true;
    saveAppSettingsToStorage(this.settings);
  }

  @Mutation
  [Mutations.SET_APP_SETTING]({ key, value }: { key: string; value: unknown }) {
    this.settings = { ...this.settings, [key]: value };
    this.isDefault = false;
    saveAppSettingsToStorage(this.settings);
  }
}
